import React, { useState, useMemo } from 'react';
import { Users, X, Calendar, User, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mccPatch } from './mccApi';
import { PROJECT_STAGES, statusTone, PRIORITIES } from './mccConstants';
import { Avatar } from './mccUi';

/**
 * Floating bulk-action bar that appears when 1+ projects are selected.
 *
 * Supports:
 *   - Assign / re-assign the "assigned_to" set (replace or add)
 *   - Set editor / approver / publisher
 *   - Set status
 *   - Set priority
 *   - Set due date
 */
export default function MccBulkActionBar({
  selection,
  onClear,
  onDone,
  team,
}) {
  const [action, setAction] = useState('');
  const [running, setRunning] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [role, setRole] = useState('editor_id');
  const [roleUser, setRoleUser] = useState('');
  const [dueDate, setDueDate] = useState('');

  if (!selection.size) return null;

  const run = async () => {
    if (!action) { toast.error('Pick an action'); return; }
    setRunning(true);
    const ids = Array.from(selection);
    let patch = {};
    if (action === 'assign_add') {
      if (!assignee) { setRunning(false); toast.error('Pick a person'); return; }
    } else if (action === 'set_role') {
      if (!role) { setRunning(false); toast.error('Pick a role'); return; }
      patch = { [role]: roleUser || null };
    } else if (action === 'set_status') {
      if (!status) { setRunning(false); toast.error('Pick a status'); return; }
      patch = { status };
    } else if (action === 'set_priority') {
      if (!priority) { setRunning(false); toast.error('Pick a priority'); return; }
      patch = { priority };
    } else if (action === 'set_due') {
      if (!dueDate) { setRunning(false); toast.error('Pick a date'); return; }
      patch = { due_date: dueDate };
    }

    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        if (action === 'assign_add') {
          // fetch current then merge
          // we do a PATCH sending assigned_to as an "add", but backend replaces the whole list.
          // So we do a compare & merge on the client via a fresh GET per project.
          const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/marketing/projects/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || localStorage.getItem('token') || ''}` },
          });
          const proj = await resp.json();
          const set = new Set(proj.assigned_to || []);
          set.add(assignee);
          await mccPatch(`/marketing/projects/${id}`, { assigned_to: Array.from(set) });
        } else {
          await mccPatch(`/marketing/projects/${id}`, patch);
        }
        ok += 1;
      } catch { fail += 1; }
    }
    setRunning(false);
    toast[ok && !fail ? 'success' : fail ? 'error' : 'info'](`${ok} updated${fail ? `, ${fail} failed` : ''}`);
    setAction('');
    onDone?.();
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-3xl w-[calc(100%-2rem)]">
      <div className="rounded-xl bg-slate-900 text-white shadow-2xl p-3 flex flex-wrap items-center gap-2 border border-slate-700">
        <div className="flex items-center gap-2 pr-2 border-r border-slate-700">
          <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-xs">{selection.size}</div>
          <p className="text-sm font-semibold">selected</p>
        </div>

        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-9 w-44 bg-slate-800 border-slate-700 text-white">
            <SelectValue placeholder="Bulk action…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="assign_add">Assign to person</SelectItem>
            <SelectItem value="set_role">Set editor / approver / publisher</SelectItem>
            <SelectItem value="set_status">Set status</SelectItem>
            <SelectItem value="set_priority">Set priority</SelectItem>
            <SelectItem value="set_due">Set due date</SelectItem>
          </SelectContent>
        </Select>

        {action === 'assign_add' && (
          <Select value={assignee} onValueChange={setAssignee}>
            <SelectTrigger className="h-9 w-48 bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Person…" /></SelectTrigger>
            <SelectContent>
              {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {action === 'set_role' && (
          <>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-9 w-36 bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="editor_id">Editor</SelectItem>
                <SelectItem value="approver_id">Approver</SelectItem>
                <SelectItem value="publisher_id">Publisher</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleUser} onValueChange={setRoleUser}>
              <SelectTrigger className="h-9 w-48 bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Person…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {team.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}

        {action === 'set_status' && (
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 w-56 bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Status…" /></SelectTrigger>
            <SelectContent>
              {PROJECT_STAGES.map((s) => <SelectItem key={s} value={s}>{statusTone(s).label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {action === 'set_priority' && (
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="h-9 w-32 bg-slate-800 border-slate-700 text-white"><SelectValue placeholder="Priority…" /></SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {action === 'set_due' && (
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
            className="h-9 w-40 bg-slate-800 border-slate-700 text-white" />
        )}

        <div className="flex-1" />

        <Button size="sm" onClick={run} disabled={running || !action}
          className="bg-indigo-500 hover:bg-indigo-700 text-white" data-testid="mcc-bulk-apply">
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
          {running ? 'Applying…' : 'Apply'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClear}
          className="text-slate-300 hover:text-white hover:bg-slate-800" data-testid="mcc-bulk-clear">
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
